<template>
    <div class="eligible-visitors">
        <span
            aria-hidden="true">
            {{ eligibleVisitorCount }}
        </span>
        <span
            class="visually-hidden"
            aria-live="polite"
            aria-atomic="true"
            :aria-busy="eligibleVisitors.isLoading">
            {{ eligibleVisitorCountAnnouncement }}
        </span>
        <pendo-loading-indicator
            :loading="eligibleVisitors.isLoading"
            size="small" />
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { LOADING_STATES } from '@/constants';
import { addCommasToNumber } from '@utils/formatters';
import { connectAggregationToState, withComponent } from '@pendo/agg-connect';
import { PendoLoadingIndicator } from '@pendo/components';
import visitorList from '@/vuex/modules/segments/segment-eligible-visitor-list.aggregation';

const EMPTY_COUNT_VALUE = '--';

export default {
    name: 'EligibleVisitorCount',
    components: {
        PendoLoadingIndicator,
    },
    props: {
        segment: {
            type: Object,
            default: null,
        },
        isValid: {
            type: Boolean,
            default: true,
        },
        groupNumber: {
            type: Number,
            default: null,
        },
    },
    emits: ['eligible-visitors-updated'],
    data () {
        return {
            livePreview: true,
            visitorCountAggSub: undefined,
            debouncedSegment: undefined,
            eligibleVisitors: {
                count: EMPTY_COUNT_VALUE,
                isLoading: false,
            },
        };
    },
    computed: {
        computedSegment () {
            if (isEmpty(this.segment)) {
                return null;
            }

            return { ...this.segment };
        },
        eligibleVisitorCount () {
            const { count } = this.eligibleVisitors;

            if (count === EMPTY_COUNT_VALUE || count === null || count === undefined || Number.isNaN(count)) {
                return EMPTY_COUNT_VALUE;
            }

            return addCommasToNumber(count);
        },
        eligibleVisitorCountAnnouncement () {
            if (this.eligibleVisitors.isLoading) {
                if (this.groupNumber !== null) {
                    return this.$t('segmentStats.Loading eligible visitor count for group {groupNumber}', { groupNumber: this.groupNumber });
                }

                return this.$t('segmentStats.Loading eligible visitor count');
            }

            if (this.groupNumber !== null) {
                return this.$t('segmentStats.Eligible visitor count for group {groupNumber}: {count}', {
                    groupNumber: this.groupNumber,
                    count: this.eligibleVisitorCount,
                });
            }

            return this.$t('segmentStats.Eligible visitor count: {count}', { count: this.eligibleVisitorCount });
        },
    },
    watch: {
        eligibleVisitors: {
            handler (newVal) {
                if(newVal.count === EMPTY_COUNT_VALUE) {
                    this.$emit('eligible-visitors-updated', { ...newVal, count: 0 });
                };
                this.$emit('eligible-visitors-updated', newVal);
            },
            deep: true,
        },
        computedSegment (segment, prevSegment) {
            if (!this.isValid) return;
            if (isEmpty(segment)) return;
            if (segment.name !== prevSegment?.name) return;

            this.debounceSegment(segment);
        },
    },
    created () {
        this.setupVisitorCountAgg();
        this.debounceSegment = debounce((segment) => {
            this.setDebouncedSegment(segment);
        }, 1500);
    },
    beforeUnmount () {
        this.visitorCountAggSub.unsubscribe();
    },
    methods: {
        loadingStatus () {
            this.eligibleVisitors.isLoading = true;
        },
        rejectedStatus () {
            this.eligibleVisitors.count = EMPTY_COUNT_VALUE;
            this.eligibleVisitors.isLoading = false;
        },
        resolvedStatus (value) {
            this.eligibleVisitors.count = value[0].eligibleVisitorCount;
            this.eligibleVisitors.isLoading = false;
        },
        setupVisitorCountAgg () {
            const agg$ = connectAggregationToState(
                visitorList,
                withComponent({ segment: 'debouncedSegment', livePreview: 'livePreview' }),
            )({ component: this });

            this.visitorCountAggSub = agg$.subscribe(({ status, value }) => {
                if (status === LOADING_STATES.LOADING) this.loadingStatus();
                if (status === LOADING_STATES.REJECTED) this.rejectedStatus();
                if (status === LOADING_STATES.RESOLVED) this.resolvedStatus(value);
            });
        },
        setDebouncedSegment (segment) {
            this.eligibleVisitors.isLoading = false;
            this.debouncedSegment = segment;
        },
    },
};
</script>

<style scoped lang="less">
.eligible-visitors {
    display: flex;
    align-items: center;

    span:not(.visually-hidden) {
        font-weight: 600;
        font-size: 22px;
        line-height: 22px;
        margin-right: 8px;
    }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
